import React from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { LanguageUtils } from '../../lib/languageUtils';
import type { Language } from '../types';

interface LanguageValidatorProps {
  children: React.ReactNode;
}

/**
 * Component that validates the language parameter from the URL
 * and redirects to the correct language if needed
 */
export const LanguageValidator: React.FC<LanguageValidatorProps> = ({ children }) => {
  const params = useParams();
  const location = useLocation();
  
  // Check if the language parameter is valid
  const langParam = params.lang as string;
  
  // If no language parameter or invalid language, redirect
  if (!langParam || !LanguageUtils.isValidLanguage(langParam)) {
    const savedLanguage = LanguageUtils.getSavedLanguage();
    const { pathWithoutLang } = LanguageUtils.extractLanguageFromPath(location.pathname);
    const redirectPath = LanguageUtils.buildLanguageUrl(savedLanguage, pathWithoutLang);
    
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

interface RedirectToLanguageProps {
  fallbackLanguage?: Language;
}

/**
 * Component that redirects to a language-prefixed URL
 */
export const RedirectToLanguage: React.FC<RedirectToLanguageProps> = ({ 
  fallbackLanguage 
}) => {
  const location = useLocation();
  
  // Use provided fallback or get saved language
  const targetLanguage = fallbackLanguage || LanguageUtils.getSavedLanguage();
  
  // Get the current path without language prefix
  const { pathWithoutLang } = LanguageUtils.extractLanguageFromPath(location.pathname);
  
  // Build the redirect URL
  const redirectPath = LanguageUtils.buildLanguageUrl(targetLanguage, pathWithoutLang);
  
  return <Navigate to={redirectPath} replace />;
};